/* eslint-disable no-nested-ternary */
// src/components/dentally/DentallyBookingSuccess.jsx
import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Alert, Divider, Stack, Collapse } from '@mui/material';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import Iconify from '../Iconify';
import PaymentLinkTracker from './PaymentLinkTracker';
import { callApi } from '../../utils/apiCall';
import { parseDentallyDate } from '../../utils/dentallyUtils';
import { projectDentallyBookingDiagnostic } from '../../utils/dentallyBookingDiagnostics';

// ── Internal step labels (shown in expandable detail) ──────────────────
const STEP_LABELS = {
  dentally_patient_created: 'Patient created in Dentally',
  lead_created: 'Lead created in CRM',
  treatment_created: 'Treatment added',
  crm_appointment_created: 'Appointment booked in CRM',
  dentally_appointment_created: 'Appointment booked in Dentally',
  payment_link_sent: 'Payment link sent',
  payment_received: 'Payment recorded',
  dentally_booking_deferred: 'Dentally booking deferred — manual action required after payment',
  dentally_reminder_created: 'Payment reminder created',
};

// ── Per-channel delivery chip metadata (contract A `channels` states) ──
const CHANNEL_LABELS = { email: 'Email', whatsapp: 'WhatsApp' };
const CHANNEL_STATE_META = {
  queued: { color: '#FFAB00', icon: 'mdi:clock-outline' },
  accepted: { color: '#FFAB00', icon: 'mdi:clock-outline' },
  sent: { color: '#00AB55', icon: 'mdi:check-circle-outline' },
  delivered: { color: '#00AB55', icon: 'mdi:check-circle' },
  read: { color: '#00AB55', icon: 'mdi:check-all' },
  failed: { color: '#FF5630', icon: 'mdi:alert-circle-outline' },
  blocked: { color: '#FF5630', icon: 'mdi:block-helper' },
};
// A `blocked` channel with this code was skipped only because the sibling
// requested channel failed prerequisites first — it's not a problem with
// this channel's own recipient/config, so it gets neutral (not error) styling.
const OTHER_CHANNEL_BLOCKED_CODE = 'not_sent_other_channel_blocked';
const OTHER_CHANNEL_BLOCKED_META = { color: '#919EAB', icon: 'mdi:minus-circle-outline' };

const formatApptTime = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = parseDentallyDate(dateStr);
    if (!d) return null;
    return d.toLocaleString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return null; }
};

// ── Derive user-meaningful summary steps from raw backend steps ────────
const getSummarySteps = (steps, apptTime, showDentallyFailed) => {
  const summary = [];
  // Appointment booked is always the first meaningful step
  if (steps.includes('crm_appointment_created') || steps.includes('dentally_appointment_created')) {
    summary.push({
      icon: 'mdi:calendar-check',
      label: apptTime ? `Appointment booked — ${apptTime}` : 'Appointment booked',
      color: '#00AB55',
    });
  }
  // Dentally warning as a summary-level item
  if (showDentallyFailed) {
    summary.push({
      icon: 'mdi:alert-circle',
      label: 'Dentally sync needs attention',
      color: '#FFAB00',
    });
  }
  // Payment
  if (steps.includes('payment_link_sent')) {
    summary.push({
      icon: 'mdi:credit-card-outline',
      label: 'Payment link sent to patient',
      color: '#00AB55',
    });
  } else if (steps.includes('payment_received')) {
    summary.push({
      icon: 'mdi:check-decagram',
      label: 'Payment recorded',
      color: '#00AB55',
    });
  }
  return summary;
};

export default function DentallyBookingSuccess({
  result,
  practiceId,
  bookingDiagnostic,
  onViewLead,
  onBookAnother,
  onClose,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isPaid, setIsPaid] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  const projectedDiagnostic = projectDentallyBookingDiagnostic(bookingDiagnostic);
  const isPatientValidation = projectedDiagnostic?.stage === 'patient_prepare';
  const isReconciliationRequired = projectedDiagnostic?.outcome === 'reconciliation_required';

  // Derive isDeferred before the early return so it can be used in hooks below
  const isDeferred = result?.isDeferred ?? false;

  // Wrap onClose to fire an informational toast for deferred bookings
  const handleClose = useCallback(() => {
    if (isDeferred) {
      const name = result?.leadName || 'Lead';
      const leadDetailPath = practiceId && result?.leadId
        ? `/dashboard/practice/${practiceId}/lead-detail/${result.leadId}`
        : null;
      enqueueSnackbar(
        `${name} has not moved status — book in Dentally manually after payment`,
        {
          variant: 'info',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          autoHideDuration: 10000,
          action: leadDetailPath ? () => (
            <Button
              size="small"
              sx={{ color: 'inherit', fontWeight: 700, ml: 1, whiteSpace: 'nowrap' }}
              onClick={() => navigate(leadDetailPath)}
            >
              View Lead
            </Button>
          ) : undefined,
        },
      );
    }
    onClose?.();
  }, [isDeferred, result?.leadName, result?.leadId, practiceId, navigate, enqueueSnackbar, onClose]);

  const handleMarkAsPaid = useCallback(async () => {
    if (!practiceId || !result?.leadId || !result?.treatmentRecordId || !result?.paymentRecordId) return;
    setMarkingPaid(true);
    try {
      const res = await callApi(
        `/api/v1/practices/${practiceId}/leads/${result.leadId}/treatments/${result.treatmentRecordId}/payments/${result.paymentRecordId}/`,
        'patch',
        { paid_at: new Date().toISOString(), mode_of_payment: 'cash' },
      );
      if (res.ok) {
        setIsPaid(true);
      }
    } catch { /* fallback — still toggle UI so user isn't stuck */ }
    setMarkingPaid(false);
  }, [practiceId, result?.leadId, result?.treatmentRecordId, result?.paymentRecordId]);

  if (!result?.success) return null;

  const apptTime = formatApptTime(result.startTime);
  const steps = result.steps ?? [];
  const showDentallyFailed = result.dentallyWarning && !steps.includes('dentally_appointment_created');
  const hasPaymentLink = steps.includes('payment_link_sent');
  const hasWarnings = !!result.dentallyWarning || !!result.paymentWarning;

  // ── Heading state ──────────────────────────────────────────────────
  const headingText = isDeferred
    ? 'Payment Link Sent — Booking Pending'
    : hasWarnings ? 'Appointment Saved — Action Needed' : 'Appointment Confirmed';
  const headingColor = isDeferred ? '#FFAB00' : hasWarnings ? '#FFAB00' : '#00AB55';
  const headingIcon = isDeferred ? 'mdi:cash-clock' : hasWarnings ? 'mdi:alert-circle' : 'mdi:check-circle';
  const headingBg = isDeferred ? 'rgba(255,171,0,0.1)' : hasWarnings ? 'rgba(255,171,0,0.1)' : 'rgba(0,171,85,0.1)';

  // ── Summary steps ──────────────────────────────────────────────────
  const summarySteps = getSummarySteps(steps, apptTime, showDentallyFailed);

  // ── Footer button logic ────────────────────────────────────────────
  const primaryLabel = isDeferred
    ? 'Close & Track Payment'
    : hasPaymentLink ? (isPaid ? 'Done' : 'Close & Track Payment') : 'Done';

  return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      {/* ─── Heading zone ─── */}
      <Box sx={{
        width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 1.5,
        bgcolor: headingBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background-color 0.4s ease',
      }}>
        <Iconify icon={headingIcon} width={32} sx={{ color: headingColor, transition: 'color 0.4s ease' }} />
      </Box>
      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: headingColor, transition: 'color 0.4s ease' }}>
        {headingText}
      </Typography>

      {apptTime && !hasWarnings && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {apptTime}
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      {isDeferred && (
        <Alert
          severity="warning"
          icon={<Iconify icon="mdi:information-outline" width={20} />}
          sx={{ textAlign: 'left', mb: 2 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Lead stays as New until payment confirmed
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {result.steps?.includes('dentally_reminder_created')
              ? 'A reminder with the booking details has been created. Once the patient pays, open the lead and book in Dentally manually.'
              : 'Once the patient pays, open the lead and book in Dentally manually.'}
          </Typography>
        </Alert>
      )}

      {/* ─── Summary steps (user-meaningful) ─── */}
      <Box sx={{ textAlign: 'left', mb: 2 }}>
        {summarySteps.map((step, idx) => (
          <Stack
            key={step.label}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              mb: 0.75,
              animation: 'fadeSlideIn 0.3s ease both',
              animationDelay: `${idx * 80}ms`,
              '@keyframes fadeSlideIn': {
                from: { opacity: 0, transform: 'translateY(6px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Iconify icon={step.icon} width={18} sx={{ color: step.color, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{step.label}</Typography>
          </Stack>
        ))}
      </Box>

      {/* ─── Per-channel delivery outcome chips (contract A `channels`) ─── */}
      {result.channelResults && Object.keys(result.channelResults).length > 0 && (
        <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mb: 2 }}>
          {Object.entries(result.channelResults)
            .filter(([, info]) => info && info.state !== 'not_requested')
            .map(([channel, info]) => {
              const isOtherChannelBlock = info.code === OTHER_CHANNEL_BLOCKED_CODE;
              const meta = isOtherChannelBlock
                ? OTHER_CHANNEL_BLOCKED_META
                : (CHANNEL_STATE_META[info.state] || { color: '#919EAB', icon: 'mdi:help-circle-outline' });
              const text = isOtherChannelBlock
                ? 'Not sent (other channel blocked)'
                : `${info.state}${info.error ? ` — ${info.error}` : ''}`;
              return (
                <Box
                  key={channel}
                  title={isOtherChannelBlock ? undefined : (info.error || undefined)}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    px: 1, py: 0.5, borderRadius: '999px',
                    bgcolor: `${meta.color}1F`, border: `1px solid ${meta.color}`,
                  }}
                >
                  <Iconify icon={meta.icon} width={14} sx={{ color: meta.color }} />
                  <Typography variant="caption" sx={{ color: meta.color, fontWeight: 600 }}>
                    {CHANNEL_LABELS[channel] || channel}: {text}
                  </Typography>
                </Box>
              );
            })}
        </Stack>
      )}

      {/* ─── Expandable detail toggle ─── */}
      {steps.length > 0 && (
        <Box sx={{ textAlign: 'left', mb: 2 }}>
          <Typography
            variant="caption"
            onClick={() => setShowDetail(prev => !prev)}
            sx={{
              color: 'text.disabled',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { color: 'text.secondary' },
              transition: 'color 0.2s ease',
              userSelect: 'none',
            }}
          >
            <Iconify
              icon={showDetail ? 'mdi:chevron-up' : 'mdi:chevron-down'}
              width={16}
              sx={{ transition: 'transform 0.2s ease' }}
            />
            {showDetail ? 'Hide details' : `Show details (${steps.length} steps)`}
          </Typography>
          <Collapse in={showDetail} timeout={250}>
            <Box sx={{ mt: 1, pl: 0.5 }}>
              {steps.map(step => (
                <Stack key={step} direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                  <Iconify icon="mdi:check" width={14} sx={{ color: '#00AB55', flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary">{STEP_LABELS[step] || step}</Typography>
                </Stack>
              ))}
              {showDentallyFailed && (
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                  <Iconify icon="mdi:alert" width={14} sx={{ color: '#FFAB00', flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary">Could not book in Dentally</Typography>
                </Stack>
              )}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* ─── Error recovery zone ─── */}
      {result.dentallyWarning && (
        <Alert
          severity="warning"
          sx={{ textAlign: 'left', mb: 2 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Dentally appointment not created
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            Your appointment is saved in the CRM but could not be booked in Dentally.
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {isPatientValidation
              ? 'Correct the patient salutation and date of birth, then restart the normal booking flow.'
              : isReconciliationRequired
                ? 'Do not retry this booking. Reconcile the outcome using the diagnostic ID before taking further action.'
                : projectedDiagnostic
                  ? 'Review the safe booking diagnostic before taking further action.'
                  : 'Contact your practice administrator before taking further action.'}
          </Typography>
        </Alert>
      )}

      {result.paymentWarning && (
        <Alert severity="warning" sx={{ textAlign: 'left', mb: 2 }}>
          Booking confirmed. Payment link failed — {result.paymentWarning}
        </Alert>
      )}

      {/* ─── Payment tracker zone (below divider, only when relevant) ─── */}
      {hasPaymentLink &&
        practiceId && result.leadId && result.treatmentRecordId && result.paymentRecordId && (
        <>
          <Divider sx={{ my: 2 }} />
          <PaymentLinkTracker
            practiceId={practiceId}
            leadId={result.leadId}
            treatmentId={result.treatmentRecordId}
            paymentId={result.paymentRecordId}
            contactEmail={result.contactEmail}
            contactPhone={result.contactPhone}
            checkoutUrl={result.checkoutUrl}
            linkChannel={result.paymentLinkChannel}
            onPaymentStatusChange={(status) => setIsPaid(status === 'paid')}
            externalPaid={isPaid}
            onMarkAsPaid={handleMarkAsPaid}
            markingPaid={markingPaid}
          />
        </>
      )}

      {/* ─── Footer: max 2 buttons ─── */}
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', alignItems: 'center', mt: 3 }}>
        {onViewLead && result.leadId != null && (
          <Button variant="outlined" onClick={() => onViewLead(result.leadId)} sx={{ fontWeight: 500 }}>
            View Lead
          </Button>
        )}
        {onClose ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleClose}
            sx={{ minWidth: 140, fontWeight: 600 }}
          >
            {primaryLabel}
          </Button>
        ) : (
          <Button variant="contained" onClick={onBookAnother} sx={{ minWidth: 140, fontWeight: 600 }}>
            Book Another
          </Button>
        )}
      </Box>
    </Box>
  );
}

DentallyBookingSuccess.propTypes = {
  result: PropTypes.shape({
    success: PropTypes.bool,
    leadId: PropTypes.number,
    leadName: PropTypes.string,
    treatmentRecordId: PropTypes.number,
    paymentRecordId: PropTypes.number,
    checkoutUrl: PropTypes.string,
    channelResults: PropTypes.object,
    contactEmail: PropTypes.string,
    contactPhone: PropTypes.string,
    paymentLinkChannel: PropTypes.string,
    paymentAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    steps: PropTypes.arrayOf(PropTypes.string),
    dentallyWarning: PropTypes.string,
    paymentWarning: PropTypes.string,
    startTime: PropTypes.string,
    isDeferred: PropTypes.bool,
  }).isRequired,
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bookingDiagnostic: PropTypes.object,
  onViewLead: PropTypes.func,
  onBookAnother: PropTypes.func,
  onClose: PropTypes.func,
};

DentallyBookingSuccess.defaultProps = {
  practiceId: null,
  bookingDiagnostic: null,
  onViewLead: null,
  onBookAnother: null,
  onClose: null,
};
